import { finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TemplateSettingsService } from '../template-settings.service';
import { GoogleAnalyticsService } from 'src/app/Services/google-analytics.service';
import { DataService } from 'src/app/Services/Data/data.service';

@Component({
  selector: 'template-settings-list',
  templateUrl: './template-settings-list.component.html',
  styleUrls: ['./template-settings-list.component.css']
})
export class TemplateSettingsListComponent implements OnInit {
  @ViewChild('myModal') modal!: ElementRef;

  is_delete_settings = false;
  user_data: any;
  targetRole = 'system administrator';
  isUserHasAccess: boolean = false;

  templateList: any[];
  pager: any = {};
  pagedItems: any[];
  selectedItem: any;
  template: any;
  salesforce_session_data: any;
  userSFData: any;
  options: any = ['edit', 'delete'];

  // Loading
  loading = {
    is_display: true,
    message: ''
  };

  // error
  error_message: string = '';

  constructor(
    private templateSettingsService: TemplateSettingsService,
    private router: Router,
    private dataService: DataService,
    private GA: GoogleAnalyticsService
  ) {}

  async ngOnInit() {
    this.dataService
      .getUserRole()
      // .finally(() => this.hideLoading())
      .subscribe(
        async (userRole: string) => {
          if (userRole && userRole.toLocaleLowerCase() === this.targetRole) {
            this.isUserHasAccess = true;

            this.selectedItem = '';
            this.salesforce_session_data = await this.templateSettingsService.getSFData();

            this.dataService.maintenanceCheck(this.salesforce_session_data);

            this.userSFData = this.templateSettingsService.getUserSfData();
            this.updateLoading(true, 'Loading Document Flow list...');
            this.checkIfUserLoggedInToSN();

            this.templateSettingsService
              .getAllTemplateSettings()
              .pipe(finalize(() => this.hideLoading()))
              .subscribe(
                (result) => {
                  this.templateList = result;
                  this.setPage(1);
                },
                () => this.updateError('Failed to load Document Flow list. Please reload the page.')
              );
          } else {
            this.hideLoading();
          }
        },
        () => (this.updateError('Something went wrong - try again!'), this.hideLoading())
      );
  }

  async checkIfUserLoggedInToSN() {
    if (this.templateSettingsService.isUserLoggedInSignNow() === null) {
      try {
        this.salesforce_session_data = this.dataService.getSalesforceData();

        // send information to db: user will be added if not already found in db otherwise user data will be updated
        if (!this.isValidSFSession(this.salesforce_session_data)) {
          this.updateLoading(false, '');
          this.updateError(this.dataService.salesforce_session_error);
          this.templateSettingsService.setIsUserLoggedIn(false);
          return;
        } else {
          this.templateSettingsService.setIsUserLoggedIn(true);
        }
      } catch (error) {
        return error;
      }
    }
  }

  isValidSFSession(sf_session_data) {
    const sfSessionKeys = Object.keys(sf_session_data);
    // return sfSessionKeys.length && sfSessionKeys.every(key => sf_session_data[key] || key === 'record_type' || key === 'record_id');

    return (
      sfSessionKeys.length &&
      sf_session_data['context'] &&
      sf_session_data['instance_url'] &&
      sf_session_data['sf_org_id'] &&
      sf_session_data['sf_refresh'] &&
      sf_session_data['sf_token'] &&
      sf_session_data['sf_user_id']
    );
  }

  async onEdit(i, settings) {
    this.template = this.routeToEditForm(i, settings);
  }

  private routeToEditForm(i: any, settings: any): any {
    this.updateLoading(true, 'Loading selected Document Flow...');
    return this.templateSettingsService
      .getTemplateSettings(i, settings)
      .pipe(finalize(() => this.hideLoading()))
      .subscribe(
        (result) => {
          this.templateSettingsService.setTemplate(result);
          const link = ['template_settings_form', result];
          this.router.navigate(link).then(
            function () {},
            function () {}
          );
        },
        () => this.updateError('Failed to load selected Document Flow. Please try again.')
      );
  }
  routeToCreateForm() {
    const link = ['template_settings_form'];
    this.router.navigate(link).then(
      function () {},
      function () {}
    );
  }

  deleteTemplate() {
    this.clearErrors();

    this.templateSettingsService.deleteTemplateSettings(this.selectedItem).subscribe(
      () => {
        this.templateSettingsService.resetTemplate();
        this.templateList = this.templateList.filter((tpl) => tpl.id !== this.selectedItem.id);
        this.setPage(this.pager.currentPage || 1);
      },
      () => {
        this.updateError(
          `Document Flow "${this.selectedItem.setting_name}" was not deleted. Please try again.`
        );
        this.closeModal();
      }
    );
  }

  setPage(page: number) {
    // get pager object from service
    this.pager = this.getPager(this.templateList.length, page);

    // get current page of items
    this.pagedItems = this.templateList.slice(this.pager.startIndex, this.pager.endIndex + 1);
    this.closeModal();
  }
  getPager(totalItems: number, currentPage: number = 1, pageSize: number = 10) {
    // calculate total pages
    const totalPages = Math.ceil(totalItems / pageSize);
    // ensure current page isn't out of range
    if (currentPage < 1) {
      currentPage = 1;
    } else if (currentPage > totalPages) {
      currentPage = totalPages;
    }

    let startPage: number, endPage: number;
    if (totalPages <= 10) {
      // less than 10 total pages so show all
      startPage = 1;
      endPage = totalPages;
    } else {
      // more than 10 total pages so calculate start and end pages
      if (currentPage <= 6) {
        startPage = 1;
        endPage = 10;
      } else if (currentPage + 4 >= totalPages) {
        startPage = totalPages - 9;
        endPage = totalPages;
      } else {
        startPage = currentPage - 5;
        endPage = currentPage + 4;
      }
    }

    // calculate start and end item indexes
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize - 1, totalItems - 1);

    // create an array of pages to ng-repeat in the pager control
    const pages = Array.from(Array(endPage + 1 - startPage).keys()).map((i) => startPage + i);

    // return object with all pager properties required by the view
    return {
      totalItems: totalItems,
      currentPage: currentPage,
      pageSize: pageSize,
      totalPages: totalPages,
      startPage: startPage,
      endPage: endPage,
      startIndex: startIndex,
      endIndex: endIndex,
      pages: pages
    };
  }

  openModal(item) {
    this.selectedItem = item;
    this.is_delete_settings = true;
  }
  closeModal() {
    this.is_delete_settings = false;
  }

  clearErrors() {
    this.error_message = '';
  }
  updateError(msg: string) {
    this.error_message = msg;
  }

  updateLoading(loadingStatus: boolean = false, msg: string = '') {
    this.loading.is_display = loadingStatus;
    this.loading.message = msg;
  }
  hideLoading() {
    this.updateLoading(false, '');
  }
}
